import { AffiliateUserWithDetails } from '@monorepo/client-types';
import {
  AffiliateCommissionWithFilterResponse,
  AffiliateFaq,
  AffiliateRequest,
  Application,
  CreateAffiliateRequestType,
  PaginationRequest,
} from '@monorepo/types';
import { BaseResponse } from '../core/base-entity-service';
import CrudService from '../core/crud-service';

interface SplitStateResponse {
  commissionPercentage: number;
  discountPercentage: number;
  isSplitActive: boolean;
}

export default class AffiliationService extends CrudService {
  static override route = 'affiliation';

  async registerAsAffiliate() {
    try {
      const response = await this.httpService.post<
        undefined,
        BaseResponse<number>
      >(`${this.path}/affiliate/register`);
      return response.data;
    } catch (e: unknown) {
      console.error('Could not register as an affiliate', e);
      throw new Error('Could not register as an affiliate');
    }
  }

  async createAffiliateRequest(data: CreateAffiliateRequestType) {
    try {
      const response = await this.httpService.post<
        CreateAffiliateRequestType,
        BaseResponse<AffiliateRequest>
      >(`${this.path}/request`, data);
      return response.data;
    } catch (e: unknown) {
      console.error('Could not create affiliate request', e);
      throw new Error('Could not create affiliate request');
    }
  }

  async getUserAffiliationDashboard() {
    try {
      const response = await this.httpService.get<
        BaseResponse<AffiliateUserWithDetails>
      >(`${this.path}/affiliate`);
      return response.data;
    } catch (e) {
      console.error('Could not fetch user affiliation dashboard', e);
      throw new Error('Could not register as an affiliate');
    }
  }

  async getAffiliateCommissions(params: PaginationRequest) {
    try {
      const response = await this.httpService.get<
        BaseResponse<AffiliateCommissionWithFilterResponse>
      >(`${this.path}/affiliate/commissions`, {
        params,
      });

      return response.data;
    } catch (e) {
      console.error('Could not fetch affiliate commissions', e);
      throw new Error('Could not fetch affiliate commissions');
    }
  }

  async getAffiliateAvailableCommissions() {
    try {
      const response = await this.httpService.get<{ data: number }>(
        `${this.path}/affiliate/commissions/available-sum`,
      );

      return response.data;
    } catch (e) {
      console.error('Could not fetch affiliate available commissions', e);
      throw new Error('Failed decrypting password');
    }
  }

  async getAffiliateFaq(application: Application): Promise<AffiliateFaq[]> {
    try {
      const response = await this.httpService.get<{
        message: string;
        data: AffiliateFaq[];
      }>(`/affiliation-faq`, {
        params: { application },
      });
      // httpService.get returns response.data, which is { message: string, data: AffiliateFaq[] }
      // So we need to extract the data field
      return response.data || [];
    } catch (e) {
      console.error('Could not fetch affiliate FAQ', e);
      throw new Error('Could not fetch affiliate FAQ');
    }
  }

  async getAffiliateCouponCode(
    affiliateId: string,
  ): Promise<{ code: string | null }> {
    try {
      const response = await this.httpService.get<{
        message: string;
        data: { code: string | null };
      }>(`${this.path}/affiliate/${affiliateId}/coupon-code`);
      return response.data;
    } catch (e) {
      console.error('Could not fetch affiliate coupon code', e);
      throw new Error('Could not fetch affiliate coupon code');
    }
  }

  async activateSplit() {
    try {
      const response = await this.httpService.post<
        undefined,
        BaseResponse<SplitStateResponse>
      >(`${this.path}/split/activate`);
      return response.data;
    } catch (e: unknown) {
      console.error('Could not activate affiliate split', e);
      throw new Error('Could not activate affiliate split');
    }
  }

  async revertSplit() {
    try {
      const response = await this.httpService.post<
        undefined,
        BaseResponse<SplitStateResponse>
      >(`${this.path}/split/revert`);
      return response.data;
    } catch (e: unknown) {
      console.error('Could not revert affiliate split', e);
      throw new Error('Could not revert affiliate split');
    }
  }
}
